import { BehaviorSubject, defer, of, Subject, Subscription } from 'rxjs';
import { CustomEditFieldComponent, Form, FormMode } from '../../../../../../base/form';
import {
  arr_filter,
  arr_first,
  arr_map,
  arr_wrap,
  blank,
  data_get,
  data_is_false,
  data_is_true,
  fallback,
  filled,
  generate_id,
  observe,
  OptionalValue,
  spf,
  transform,
  data_omit,
  data_nullify,
} from '../../../../../../shared/utils/common';
import { FormGroup } from '@angular/forms';
import { ApiService } from '../../../../../../services/api.service';
import { FormService, StudioFormTab } from '../../../../../../services/form.service';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { debounceTime, distinctUntilChanged, take, tap } from 'rxjs/operators';
import { Checklist, ChecklistAssetType, ChecklistsGenerateWithArtificationIntelligenceGeneratedEvent } from '../../../../../checklists';
import { UpdateChecklistComponentOnChangedEvent } from '../../../../../../admin/checklists/update-checklist/update-checklist.component';
import { FieldmagicDropdownOption } from '../../../../../../shared/components/forms/input/dropdown/dropdown.component';
import { AssetTypeVariant } from '../../../types';

@Component({
  selector: 'asset-types-inspection-checklist-input',
  template: `
    <div class="d-flex d-flex-gap-2x flex-column">
      <div fieldmagicGridRow>
        <div
          fieldmagicGridColumn="12"
          class="d-flex d-flex-gap justify-content-between align-items-center"
        >
          <div class="d-flex d-flex-gap w-50 align-items-center">
            <fieldmagic-dropdown-input
              class="w-50"
              [(ngModel)]="variant"
              [options]="variants$ | async"
              [standalone]="false"
              (ngModelChange)="onVariantChange($event)"
              placeholder="Select a variant.."
              label="Variant"
            >
            </fieldmagic-dropdown-input>

            <fieldmagic-dropdown-input
              class="w-50"
              [(ngModel)]="checklist"
              [options]="checklists$ | async"
              [standalone]="false"
              (ngModelChange)="onChecklistChanged()"
              placeholder="Select a checklist.."
              label="Checklist"
              [clearable]="false"
            >
            </fieldmagic-dropdown-input>
          </div>
          <div class="d-flex d-flex-gap w-35 align-items-center">
            <fieldmagic-primary-button
              variant="link"
              [icon]="[{
                icon: 'plus-circle',
                klass: 'text-success'
              }]"
              (click)="onAddChecklist()"
              label="Add Checklist"
            >
            </fieldmagic-primary-button>

            <convert-pdf-with-ai-button
              (uploaded)="onGenerate($event)"
              [moduleOrigin]="'checklist'"
              [purpose]="'pdf_checklist_ai'"
              [data]="{
                'checklist_type': 'asset',
                'mode': mode$ | async,
                'specifics': specifics,
                'asset_type_name': assetTypeName,
                'force_preview': true
              }"
            >
            </convert-pdf-with-ai-button>

            <checklists-generate-with-ai-button
              [data]="{
                'checklist_type': 'asset',
                'mode': mode$ | async,
                'specifics': specifics,
                'asset_type_name': assetTypeName,
                'force_preview': true
              }"
              (generated)="onGenerate($event)"
            >
            </checklists-generate-with-ai-button>
          </div>
        </div>

        <div
          *ngIf="fetching$ | async"
          fieldmagicGridColumn="12"
          class="d-flex justify-content-center"
        >
          <fieldmagic-icon [spin]="true"></fieldmagic-icon>
        </div>

        <div
          *ngIf="! (fetching$ | async)"
          fieldmagicGridColumn="12"
          class="d-flex justify-content-center"
        >
          <fieldmagic-text [content]="error$ | async"></fieldmagic-text>
        </div>

        <ng-container *ngIf="(! (fetching$ | async)) && (error$ | async | blank)">
          <div
            *ngIf="checklist$ | async as checklist"
            fieldmagicGridColumn="12"
          >
            <app-update-checklist
              [asInput]="true"
              [checklist]="checklist"
              [assetType]="assetType$ | async"
              (changed)="onInputChange($event)"
            >
            </app-update-checklist>
          </div>
        </ng-container>
      </div>
    </div>
  `,
})
export class AssetTypeInspectionChecklistInputComponent implements OnInit, OnDestroy, CustomEditFieldComponent {
  variant?: OptionalValue<AssetTypeInspectionVariantOption>;
  checklist?: OptionalValue<AssetTypeInspectionChecklistOption>;

  readonly checklist$ = new BehaviorSubject<OptionalValue<Checklist>>(null);
  readonly assetType$ = new BehaviorSubject<OptionalValue<ChecklistAssetType>>(null);
  readonly variants$ = new BehaviorSubject<AssetTypeInspectionVariantOption[]>([]);
  readonly checklists$ = new BehaviorSubject<AssetTypeInspectionChecklistOption[]>([]);

  readonly fetching$ = new BehaviorSubject<boolean>(true);
  readonly generating$ = new BehaviorSubject<boolean>(false);

  readonly error$ = new BehaviorSubject<OptionalValue<string>>(null);

  /// this is differ to the form mode. this controls how the generate with ai button shows
  /// the confirmation dialog stating to user that current checklist will be overriden
  readonly mode$ = new BehaviorSubject<string>('add');

  get specifics(): OptionalValue<string> {
    const standard = this._applicableStandard$.value;

    if (blank(standard)) {
      return null;
    }

    return spf('Use %s standard', {
      args: [standard],
    });
  }

  get assetTypeName(): OptionalValue<string> {
    const name = this._name$.value;

    if (blank(name)) {
      return null;
    }

    return name;
  }

  private readonly _mode$ = new BehaviorSubject<FormMode>('add');
  private readonly _field$ = new BehaviorSubject<OptionalValue<Form<any>>>(null);
  private readonly _parent$ = new BehaviorSubject<OptionalValue<FormGroup>>(null);
  private readonly _data$ = new BehaviorSubject<AssetTypeInspectionChecklistInputData>({});
  private readonly _name$ = new BehaviorSubject<OptionalValue<string>>(null);
  private readonly _applicableStandard$ = new BehaviorSubject<OptionalValue<string>>(null);
  private readonly _variant$ = new Subject<OptionalValue<AssetTypeInspectionVariantOption>>();

  private _onFetchingChecklistsSubscription?: Subscription;
  private _onNameFieldChangeSubscription?: Subscription;
  private _onApplicationStandardsFieldChangeSubscription?: Subscription;
  private _onCustomAttributesFieldChangeSubscription?: Subscription;
  private _onVariantsFieldChangeSubscription?: Subscription;
  private _onVariantFieldChangeSubscription?: Subscription;
  private _onFaultsFieldChangeSubscription?: Subscription;

  constructor(
    private readonly _api: ApiService,
    private readonly _forms: FormService,
  ) {}

  ngOnInit(): void {
    if (this._mode$.value == 'edit') {
      this._initChecklist();
    } else {
      this.fetching$.next(false);
      this.checklist$.next(this._initEmptyChecklist());
    }

    this._listenToFieldChanges();

    this._onVariantFieldChangeSubscription = this._variant$.pipe(
      distinctUntilChanged(),
    ).subscribe(() => this._initChecklist());

    this.mode$.next(this._mode$.value == 'edit' ? 'edit' : 'add');
  }

  ngOnDestroy(): void {
    if (this._onFetchingChecklistsSubscription) {
      this._onFetchingChecklistsSubscription.unsubscribe();
    }

    if (this._onNameFieldChangeSubscription) {
      this._onNameFieldChangeSubscription.unsubscribe();
    }

    if (this._onApplicationStandardsFieldChangeSubscription) {
      this._onApplicationStandardsFieldChangeSubscription.unsubscribe();
    }

    if (this._onCustomAttributesFieldChangeSubscription) {
      this._onCustomAttributesFieldChangeSubscription.unsubscribe();
    }

    if (this._onVariantsFieldChangeSubscription) {
      this._onVariantsFieldChangeSubscription.unsubscribe();
    }

    if (this._onVariantFieldChangeSubscription) {
      this._onVariantFieldChangeSubscription.unsubscribe();
    }

    if (this._onFaultsFieldChangeSubscription) {
      this._onFaultsFieldChangeSubscription.unsubscribe();
    }
  }

  /**
   * @inheritdoc
   */
  setFormMode = (mode: FormMode) => this._mode$.next(mode);

  /**
   * @inheritdoc
   */
  setParentForm = (parent: FormGroup) => this._parent$.next(parent);

  /**
   * @inheritdoc
   */
  setField = (field: Form<any>) => this._field$.next(field);

  /**
   * @inheritdoc
   */
  setAdditionalData = (data: AssetTypeInspectionChecklistInputData) => this._data$.next(data);

  onInputChange = (event: UpdateChecklistComponentOnChangedEvent) => of(event).pipe(
    take(1),
    debounceTime(300),
    distinctUntilChanged(),
    tap(console.info),
  ).subscribe((checklist) => {
    const key = fallback(data_get(checklist, 'id'), {
      to: () => generate_id(),
    });

    this._parent$.value.setValue({
      checklist: {
        ... fallback(data_get(this._parent$.value, 'value.checklist'), {
          fallback: () => ({}),
        }),
        [key]: checklist,
      },
    });
  });

  onGenerate = (event: ChecklistsGenerateWithArtificationIntelligenceGeneratedEvent) => this.checklist$.next({
    ... event,
    ... (filled(data_get(this.checklist$.value, 'id')) && {
      id: data_get(this.checklist$.value, 'id'),
    }),
    ... (filled(data_get(this.checklist$.value, 'name')) && {
      name: data_get(this.checklist$.value, 'name'),
    }),
    asset_type_id: data_get(this.assetType$.value, 'id'),
    asset_type_text: data_get(this.assetType$.value, 'name'),
    asset_type_attributes: fallback(data_get(this.assetType$.value, 'attributes'), {
      fallback: () => [],
    }),
    asset_type_severities: data_get(this.assetType$.value, 'asset_type_severities'),
    asset_type_variant_id: data_get(this.variant, 'id'),
    asset_type_variant_text: data_get(this.variant, 'text'),
  });

  onVariantChange(variant: OptionalValue<AssetTypeInspectionVariantOption>): void {
    this.checklist = null;
    this.checklists$.next([]);

    this._variant$.next(variant);
  }

  onAddChecklist(): void {
    const checklist = this._initEmptyChecklist();

    this.checklist$.next(checklist);

    this.checklists$.next([
      ... this.checklists$.value,
      ... [{
        id: checklist.id,
        text: checklist.name,
      }],
    ]);

    this.checklist = {
      id: checklist.id,
      text: checklist.name,
    };
  }

  onChecklistChanged = () => this._initChecklist();

  private _listenToFieldChanges(): void {
    const tabs: StudioFormTab[] = fallback(this._forms.tabs, {
      fallback: () => [],
    });

    for (const tab of tabs) {
      for (const field of tab.fields) {
        if (field.key == 'name') {
          /// set the current
          this._name$.next(field.default_value);

          this._onNameFieldChangeSubscription = tab.groups.controls['name'].valueChanges.pipe(
            debounceTime(300),
            distinctUntilChanged(),
          ).subscribe((value) => {
            this._name$.next(value);

            if (filled(value)) {
              this.checklist$.next({
                ... this.checklist$.value,
                name: value,
              });
            }
          });
        } else if (field.key == 'applicable_standards') {
          /// set the current
          this._applicableStandard$.next(field.default_value);

          this._onApplicationStandardsFieldChangeSubscription = tab.groups.controls['applicable_standards'].valueChanges.pipe(
            debounceTime(300),
            distinctUntilChanged(),
          ).subscribe((value) => this._applicableStandard$.next(value));
        } else if (field.key == 'attributes') {
          this._onCustomAttributesFieldChangeSubscription = tab.groups.controls['attributes'].valueChanges.pipe(
            debounceTime(300),
            distinctUntilChanged(),
          ).subscribe((value) => this.assetType$.next({
            ... fallback(this.assetType$.value, {
              fallback: () => ({
                name: this._name$.value,
              }),
            }),
            attributes: value,
          }));
        } else if (field.key == 'variants') {
          this.variants$.next(arr_map(field.default_value, {
            transformer: (value: AssetTypeVariant) => ({
              id: value.id,
              text: value.name,
            }),
          }));

          this._onVariantsFieldChangeSubscription = tab.groups.controls['variants'].valueChanges.pipe(
            debounceTime(300),
            distinctUntilChanged(),
          ).pipe(
            tap(() => this.variant = null),
            tap((variants) => {
              const deletes = arr_filter((variants), {
                predicate: (variant) => data_is_true(data_get(variant, 'deleted'))
              });

              if (blank(deletes)) {
                return;
              }

              let checklists = fallback(data_get(this._parent$.value, 'value.checklist'), {
                fallback: () => ({}),
              });

              if (blank(checklists)) {
                return;
              }

              for (const deleted of deletes) {
                if (blank(data_get(checklists, data_get(deleted, 'id')))) {
                  continue;
                }

                checklists = data_omit(checklists, {
                  keys: data_get(deleted, 'id'),
                });
              }

              this._parent$.value.setValue({
                checklist: checklists,
              });

              this.checklist$.next(fallback(data_get(checklists, '__main'), {
                fallback: () => this._initEmptyChecklist(),
              }));
            }),
          )
          .subscribe((values) => this.variants$.next(
            arr_map(
              arr_filter(values, {
                predicate: (value) => data_is_false(data_get(value, 'deleted')) && filled(data_get(value, 'name')),
              }),
              {
                transformer: (value: AssetTypeVariant) => ({
                  id: value.id,
                  text: value.name,
                }),
              }
            )
          ));
        } else if (field.key == 'fault_codes') {
          this.assetType$.next({
            ... this.assetType$.value,
            asset_type_severities: fallback(field.default_value, {
              fallback: () => [],
            }),
          });

          this._onFaultsFieldChangeSubscription = tab.groups.controls['fault_codes'].valueChanges.pipe(
            debounceTime(300),
            distinctUntilChanged(),
          ).subscribe((values) => {
            const severities = arr_filter(values, {
              predicate: (value) => data_is_false(data_get(value, 'deleted')),
            });

            this.assetType$.next({
              ... this.assetType$.value,
              asset_type_severities: severities,
            });

            this.checklist$.next({
              ... this.checklist$.value,
              asset_type_severities: severities,
            });
          });
        }
      }
    }
  }

  private _initChecklist = () => this._onFetchingChecklistsSubscription = observe({
    before: () => this.fetching$.next(true),
    after: () => this.fetching$.next(false),
    observable: () => defer(() => {
      /// reset error
      this.error$.next(null);

      const updates = fallback(data_get(this._parent$.value, 'value.checklist'), {
        fallback: () => ({}),
      });

      const key =  fallback(data_get(this.checklist, 'id'), {
        fallback: () => generate_id(),
      });

      const updated = transform(data_get(updates, key), {
        transformer: (checklist) => arr_wrap(checklist),
      });

      if (this._mode$.value == 'add' || filled(updated)) {
        return of(updated);
      }

      return this._api.callPrivate$({
        action: 'post',
        path: '/asset_types/inspection_checklists',
        expectsNewFormat: true,
        body: {
          'checklist_id': data_nullify(data_get(this.checklist, 'id')),
          'asset_type_id': this._data$.value.record_id,
          'asset_type_variant_id': data_get(this.variant, 'id'),
        },
        onSuccessResponse: (response) => response.get('items'),
      });
    }),
  })
  .subscribe((checklists) => {
    if (blank(checklists)) {
      this.mode$.next('add');
      this.checklist$.next(this._initEmptyChecklist());
      this.checklists$.next([]);
      this.checklist = null;

      return;
    }

    if (blank(this.checklist)) {
      this.checklists$.next(arr_map(checklists, {
        transformer: (checklist) => ({
          id: data_get(checklist, 'id'),
          text: data_get(checklist, 'name'),
        }),
      }));
    }

    this.checklist = transform(arr_first(checklists), {
      transformer: (checklist) => ({
        id: data_get(checklist, 'id'),
        text: data_get(checklist, 'name'),
      }),
    });

    this.checklist$.next(arr_first(checklists));
  });

  private _initEmptyChecklist(): Checklist {
    let name = this._name$.value;

    if (filled(this.variant)) {
      name = spf('%s (%s)', {
        args: [name, data_get(this.variant, 'text')],
      });
    }

    return {
      id: generate_id(),
      name: name,
      is_checklist_enabled: true,
      questions: [],
      type: 'asset',
      asset_type_id: data_get(this.assetType$.value, 'id'),
      asset_type_text: data_get(this.assetType$.value, 'name'),
      asset_type_attributes: fallback(data_get(this.assetType$.value, 'attributes'), {
        fallback: () => [],
      }),
      asset_type_severities: data_get(this.assetType$.value, 'asset_type_severities'),
      asset_type_variant_id: data_get(this.variant, 'id'),
      asset_type_variant_text: data_get(this.variant, 'text'),
    };
  }
}

type AssetTypeInspectionChecklistInputData = {
  record_id?: string;
}

type AssetTypeInspectionVariantOption = FieldmagicDropdownOption<{
  id: string;
}>;

type AssetTypeInspectionChecklistOption = FieldmagicDropdownOption<{
  id: string;
}>;